import { Button, Modal, Upload } from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined, CameraOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { compressImage, dataUrlToFile, getBase64, prepareFilesStatuses } from 'utils/file';
import { isMobileDevice } from 'utils/device';
import { ensureFreshSession, getAuthorizationHeader } from 'app/api';
import { useTranslation } from 'react-i18next';
import { showError, showSuccess, showWarning } from 'utils/notifications';

import _ from 'underscore';
import saveAs from 'file-saver';
import './styles.less';

import type { RcFile, UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import Gallery from 'components/FileUpload/component/Gallery';
import AnnotationDrawer from 'components/FileUpload/component/AnnotationDrawer';

export enum FileType {
  IMAGE,
  ALL,
  PDF,
  ATTACHMENT,
  DOCUMENT,
}

// Worker documents: a photo of a card, a PDF certificate or a Word report.
export const DOCUMENT_ACCEPT = 'image/*,application/pdf,.doc,.docx';
export const isDocumentFile = (file: { type: string; name: string }) =>
  /^image\//i.test(file.type) || file.type === 'application/pdf' || /\.docx?$/i.test(file.name);

export enum RenderType {
  GALLERY
}

type actionButtons = {
  showDownload: boolean;
  showPreview: boolean;
  showDelete: boolean;
}

type FileUploadProps = {
  uploadUrl?: string,
  limit?: number,
  type?: FileType,
  initialValues?: UploadFile[],
  deleteCallback?: (id: string) => void,
  listType?: any,
  renderType?: RenderType,
  handleSuccess?: () => void,
  actions: actionButtons,
  readonly?: boolean,
  reopenAfterUpload?: boolean,
  capture?: boolean | 'user' | 'environment' | undefined,
};

const FileUpload = (props: FileUploadProps) => {
  const {
    uploadUrl,
    deleteCallback,
    limit,
    type,
    initialValues,
    listType,
    handleSuccess,
    actions,
    renderType,
    capture,
    reopenAfterUpload,
  } = props;

  const { t } = useTranslation();

  const [ previewOpen, setPreviewOpen ] = useState(false);
  const [ previewImage, setPreviewImage ] = useState('');
  const [ fileList, setFileList ] = useState<UploadFile[]>(prepareFilesStatuses(initialValues));
  // Always-current copy of fileList: antd's next onChange can arrive before React has applied the previous update.
  const fileListRef = useRef(fileList);
  const updateFileList = (files: UploadFile[]) => {
    fileListRef.current = files;
    setFileList(files);
  };
  // Server ids of uploaded files by list uid. The uid itself stays unchanged, antd keys the tiles by it.
  const serverIdsRef = useRef(new Map<string, string>());

  const [ annotationSaving, setAnnotationSaving ] = useState(false);
  const [ annotationEditor, setAnnotationEditor ] = useState<{
    file: RcFile;
    imageSrc: string;
    resolve: (file: RcFile | Blob) => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  // Parents build initialValues on every render: sync only when the stored files changed, keep running uploads.
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;
  const initialValuesKey = _.map(initialValues ?? [], (file) => file.uid).join('|');

  useEffect(() => {
    const files = [ ...prepareFilesStatuses(initialValuesRef.current), ...fileListRef.current.filter((file) => file.status === 'uploading') ];
    fileListRef.current = files;
    setFileList(files);
  }, [ initialValuesKey ]);

  const headersRef = useRef({ 'Authorization': getAuthorizationHeader() || '' });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastOpenedWithCameraRef = useRef(false);

  const isImageType = type === FileType.IMAGE || type === FileType.ATTACHMENT;
  const showCameraAction = useMemo(() => isImageType && isMobileDevice(), [ isImageType ]);

  const handleCancel = () => {
    setPreviewOpen(false);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) file.preview = await getBase64(file.originFileObj as RcFile);
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps['onChange'] = ({ file: currentFile }) => {
    // antd 4 builds the onChange list from a fileList prop that React 18 may not have updated yet, so with several
    // uploads running one file's "done" used to overwrite another's. Apply only the changed file to our own list.
    const current = fileListRef.current;
    const changed = { ...currentFile };
    if (changed.error) changed.error = t('common.upload.failed');

    if (changed.status === 'done' && changed.response?.FileList) {
      // The API answers with the whole stored list and phone photos are all named alike ("image.jpg"):
      // take the same-named entry that is not shown yet.
      const shown = new Set(_.map(current, (f) => serverIdsRef.current.get(f.uid) ?? f.uid));
      const sameName = _.filter(changed.response.FileList, (info: any) => String(info.FileName ?? '').replace(/['"]/g, '') === changed.name);
      const uploaded = _.find(sameName, (info: any) => !shown.has(String(info.Id))) ?? _.last(sameName);
      if (uploaded) {
        changed.url = uploaded.AbsoluteUri;
        serverIdsRef.current.set(changed.uid, String(uploaded.Id));
      }
    }

    const exists = _.some(current, (f) => f.uid === currentFile.uid);
    let files = currentFile.status === 'removed'
      ? _.filter(current, (f) => f.uid !== currentFile.uid)
      : exists ? _.map(current, (f) => (f.uid === currentFile.uid ? changed : f)) : [ ...current, changed ];
    if (limit === 1) files = files.slice(-1);

    const wasUploading = _.some(current, (f) => f.status === 'uploading');
    const stillUploading = _.some(files, (f) => f.status === 'uploading');

    if (currentFile.status === 'removed') showSuccess(t('common.upload.removed'));
    if (wasUploading && !stillUploading && currentFile.status === 'done') {
      showSuccess(t('common.upload.success'));

      if (handleSuccess) handleSuccess();
      if (reopenAfterUpload) reopen();
    }
    if (currentFile.status === 'error') showError(t('common.upload.failed'));

    updateFileList(files);
  };

  const openFileDialog = (withCamera: boolean) => {
    const input = wrapperRef.current?.querySelector<HTMLInputElement>('.ant-upload input[type="file"]');
    if (!input) return;

    if (withCamera) input.setAttribute('capture', capture === 'user' ? 'user' : 'environment');
    else input.removeAttribute('capture');

    lastOpenedWithCameraRef.current = withCamera;
    input.click();
  };

  const reopen = () => openFileDialog(lastOpenedWithCameraRef.current);

  const handleRemove = async (file: UploadFile): Promise<boolean> => {
    const { confirm } = Modal;

    return new Promise((resolve) => {
      confirm({
        title: t('common.deleteconfirm', { content: '' }),
        onOk: () => {
          if (!file.error && deleteCallback) void deleteCallback(serverIdsRef.current.get(file.uid) ?? file.uid);
          resolve(true);
        },
        onCancel: () => resolve(false),
      });
    });

  };

  const handleDownload = (file: UploadFile) => {
    if (file.url) saveAs(file.url, file.name.replaceAll('"', ''));
  };

  const accept = useMemo(() => {
    if (type === FileType.ALL) return undefined;
    if (type === FileType.PDF) return 'application/pdf';
    if (type === FileType.IMAGE || type === FileType.ATTACHMENT) return 'image/*';
    if (type === FileType.DOCUMENT) return DOCUMENT_ACCEPT;
    return undefined;
  }, [ type ]);

  const withFreshToken = async (file: RcFile | Blob | string) => {
    if (file === Upload.LIST_IGNORE) return file;
    await ensureFreshSession();
    headersRef.current.Authorization = getAuthorizationHeader() || '';
    return file;
  };

  const beforeUpload = async (file: RcFile) => {
    if (limit && fileList.length >= limit) {
      showWarning(t('common.upload.maxFiles', { limit }));
      return Upload.LIST_IGNORE;
    }

    let isValid = !type || type === FileType.ALL;

    if (type === FileType.IMAGE || type === FileType.ATTACHMENT) isValid = /^(image)\//i.test(file.type);
    if (type === FileType.PDF) isValid = file.type === accept;
    if (type === FileType.DOCUMENT) isValid = isDocumentFile(file);

    if (!isValid) {
      showWarning(t('common.upload.invalid', { filename: file.name }));
      return Upload.LIST_IGNORE;
    }

    const shouldAnnotate = type === FileType.ATTACHMENT && !props.readonly;

    if (shouldAnnotate) {
      const annotated = await new Promise<RcFile | Blob>((resolve, reject) => {
        getBase64(file)
          .then((imageSrc) => {
            setAnnotationSaving(false);
            setAnnotationEditor({ file, imageSrc, resolve, reject });
          })
          .catch((error) => {
            showError(t('common.upload.failed'));
            reject(error);
          });
      });
      return withFreshToken(annotated);
    }

    if (type === FileType.IMAGE) return withFreshToken(await compressImage(file));

    return withFreshToken(file);
  };

  const uploadButton = () => (
    isImageType ?
      <div className="upload-split">
        <div className="upload-split__action" onClick={() => openFileDialog(false)}>
          <PlusOutlined />
          <div>{t('common.upload.title')}</div>
        </div>
        {showCameraAction && (
          <div className="upload-split__action" onClick={() => openFileDialog(true)}>
            <CameraOutlined />
            <div>{t('common.upload.takephoto')}</div>
          </div>
        )}
      </div>
      :
      <Button type="primary" icon={<UploadOutlined />}>
        {t('common.upload.title')}
      </Button>
  );

  const handleAnnotationComplete = async (dataUrl: string, customName?: string) => {
    if (!annotationEditor) return;
    setAnnotationSaving(true);
    try {
      const annotatedFile = dataUrlToFile(dataUrl, annotationEditor.file.name, customName) as RcFile;
      Object.assign(annotatedFile, { uid: annotationEditor.file.uid });

      const processedFile = type === FileType.IMAGE || type === FileType.ATTACHMENT
        ? await compressImage(annotatedFile) as RcFile
        : annotatedFile;

      Object.assign(processedFile, { uid: annotationEditor.file.uid });
      annotationEditor.resolve(processedFile);
      setAnnotationEditor(null);
    } catch (error) {
      annotationEditor.reject?.(error);
      showError(t('common.upload.failed'));
      setAnnotationEditor(null);
    } finally {
      setAnnotationSaving(false);
    }
  };

  const handleAnnotationCancel = () => {
    if (annotationEditor) {
      annotationEditor.resolve(Upload.LIST_IGNORE as unknown as RcFile);
      setAnnotationEditor(null);
    }
    setAnnotationSaving(false);
  };

  const renderContent = () => {
    const shouldRenderGallery = renderType === RenderType.GALLERY ||
      (type === FileType.ATTACHMENT && listType === 'picture-card');

    if (shouldRenderGallery && fileList) {
      return (
        <Gallery
          galleryOpen={previewOpen}
          onVisibleChange={setPreviewOpen}
          fileList={fileList}
          previewImage={previewImage}
        />
      );
    }

    return (
      <Modal open={previewOpen} title={t('common.preview')} footer={null} onCancel={handleCancel}>
        <img alt="preview" className='image-preview' src={previewImage} />
      </Modal>
    );
  };

  const handlePreviewIcon = (file: UploadFile) => {
    if (file.error) return <></>;
    return <EyeOutlined />;
  };

  const handleRemoveIcon = (file: UploadFile) => {
    if (file.error) return <></>;
    return <DeleteOutlined />;
  };

  const handleDownloadIcon = (file: UploadFile) => {
    if (file.error) return <></>;
    return <DownloadOutlined />;
  };

  return (
    <div ref={wrapperRef}>
      <Upload
        action={uploadUrl}
        openFileDialogOnClick={!isImageType}
        fileList={fileList}
        listType={listType}
        onPreview={actions.showPreview ? handlePreview : undefined}
        onChange={handleChange}
        onRemove={actions.showDelete ? handleRemove : undefined}
        onDownload={actions.showDownload ? handleDownload : undefined}
        beforeUpload={beforeUpload}
        accept={accept}
        headers={headersRef.current}
        multiple={type !== FileType.ATTACHMENT && (limit ? limit > 1 : false)}
        maxCount={limit}
        capture={capture}
        showUploadList={{
          showDownloadIcon: actions.showDownload,
          showPreviewIcon: actions.showPreview,
          showRemoveIcon: actions.showDelete,
          previewIcon: (file: UploadFile) => handlePreviewIcon(file),
          removeIcon: (file: UploadFile) => handleRemoveIcon(file),
          downloadIcon: (file: UploadFile) => handleDownloadIcon(file)
        }}
      >
        {fileList.length >= (limit || 1) || props.readonly ? null : uploadButton()}
      </Upload>
      {renderContent()}
      {type === FileType.ATTACHMENT && annotationEditor && (
        <AnnotationDrawer
          open
          imageSrc={annotationEditor.imageSrc}
          onCancel={handleAnnotationCancel}
          onSave={handleAnnotationComplete}
          saving={annotationSaving}
          title={t('common.upload.annotate')}
          defaultTitle={annotationEditor.file.name}
        />
      )}
    </div>
  );
};

export default FileUpload;